/**
 * Shared capacity helpers — colour thresholds and current-month utilisation
 * extraction from the UtilizationHeatmapResponse. Used by CapacityPanel and
 * any future widgets that need to colour-code member utilisation.
 *
 * All returned colour strings are CSS custom-property references (token-pure,
 * no raw hex).
 */
import type { UtilizationHeatmapResponse } from '@/api/dashboard';

/**
 * Capacity-bar fill colour by month utilisation %:
 *   < 33 → green (under-utilised)
 *   < 66 → amber (approaching capacity)
 *   ≥ 66 → red  (at / over capacity)
 */
export function capacityBarColor(utilizationPct: number): string {
  if (utilizationPct < 33) return 'var(--health-green)';
  if (utilizationPct < 66) return 'var(--health-amber)';
  return 'var(--health-red)';
}

/** Count of resources fully booked this month: current-month utilisation >= 100%. */
export function countFullyBooked(utilByMember: Record<string, number>): number {
  return Object.values(utilByMember).filter((u) => u >= 100).length;
}

/**
 * Map memberId → current (UTC) month utilizationPct from the heatmap.
 * Members with no cell for the current month default to 0.
 * Returns an empty object when the heatmap is undefined / not yet loaded.
 */
export function currentMonthUtilByMember(
  heatmap: UtilizationHeatmapResponse | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!heatmap) return out;
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1; // 1-based, matches the backend month field
  for (const member of heatmap.members) {
    const cell = member.months.find((mo) => mo.year === y && mo.month === m);
    out[member.memberId] = cell ? cell.utilizationPct : 0;
  }
  return out;
}
